import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { HelpCircle } from "lucide-react";

/**
 * Persistent help button. Opens a modal that summarizes how to use Measure X-Ray
 * without navigating the user away from their current work.
 *
 * California-only. No multi-state references.
 */
export default function HelpButton({ context = "default" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="help-button"
        className="fixed bottom-4 right-4 z-40 flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wider rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--paper))] shadow-md hover:opacity-90"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        Help
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="help-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">How to use Measure X-Ray</DialogTitle>
            <DialogDescription className="text-xs font-mono uppercase tracking-wider">
              California bills + ballot measures · Hostile-review audit
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-[hsl(var(--ink-2))] leading-relaxed space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <p>
              Measure X-Ray reads a California bill or ballot measure the way a
              hostile contract reviewer would. It does not tell you how to
              vote — it shows you what is actually in the text, where the
              power and the cost are buried, and how public claims hold up
              against the bill.
            </p>
            <Section title="Find a bill or measure">
              <ul className="list-disc list-outside ml-5 space-y-1">
                <li>Type a California bill number (AB&nbsp;25, SB&nbsp;42, ACA&nbsp;13, etc.).</li>
                <li>Or browse qualified / eligible California ballot measures.</li>
                <li>Or paste an official government URL (LegInfo, Secretary of State, LAO).</li>
              </ul>
            </Section>
            <Section title="Pasted-text fallback">
              <p>
                If the official source cannot be fetched — scanned PDF, blocked
                site, image-only document — paste the full official text into
                the textarea. Measure X-Ray will analyze pasted text the same
                way as a fetched source.
              </p>
            </Section>
            <Section title="Read the report">
              <ul className="list-disc list-outside ml-5 space-y-1">
                <li><strong>Plain-English Summary</strong> — what the bill appears to do.</li>
                <li><strong>Hidden Cost / Future Fee Watch</strong> — buried fees, funds, future guidance, agency authority, exemptions, penalties, contributions.</li>
                <li><strong>Public Claim Reality Check</strong> — paste a claim, get a verdict against the bill text.</li>
                <li><strong>Changes to Current Law</strong> — code sections added/amended/repealed, side-by-side.</li>
                <li><strong>Who Gains Power / Who Pays</strong> — who gets authority, who bears cost.</li>
                <li><strong>Citations</strong> — official source, bill version, page (when PDF), section, code reference. Click any quote to see the surrounding paragraph.</li>
              </ul>
            </Section>
            <Section title="Verify everything yourself">
              <p>
                Each quote card includes the official source, bill version,
                page number when available, section number, and code section
                when available. For HTML sources without page numbers, use the
                section/code reference and official source link. Click any
                quote to expand it to the full surrounding paragraph.
              </p>
            </Section>
            <Section title="Free for voters">
              <p>
                Core voter analysis is free. Optional support helps keep the
                tool running. Custom dossiers are paid deep-dive reports — but
                basic bill and measure analysis is never paywalled.
              </p>
            </Section>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] mb-1">
        {title}
      </div>
      {children}
    </div>
  );
}
